import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, RotateCcw, Trash2, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Tables } from "@/integrations/supabase/types";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { useAuth } from "@/hooks/useAuth";

type DeletedVehicle = Tables<"deleted_vehicles">;

interface VehicleSnapshot {
  year: number;
  make: string;
  model: string;
  vin: string;
  price: number;
  mileage: number;
  color: string;
  status: string;
}

export default function RecycleBin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [deletedVehicles, setDeletedVehicles] = useState<DeletedVehicle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [restoreTarget, setRestoreTarget] = useState<{ id: string; name: string } | null>(null);
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    loadDeletedVehicles();
  }, []);

  const loadDeletedVehicles = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("deleted_vehicles")
      .select("*")
      .is("restored_at", null)
      .order("deleted_at", { ascending: false });

    if (error) {
      toast.error("Failed to load recycle bin");
    } else {
      setDeletedVehicles(data || []);
    }
    setIsLoading(false);
  };

  const restoreVehicle = async (deletedVehicleId: string) => {
    setIsProcessing(true);
    const { error } = await supabase.rpc("restore_vehicle", {
      p_deleted_vehicle_id: deletedVehicleId,
      p_restored_by: user?.id || "",
    });

    if (error) {
      toast.error("Failed to restore vehicle: " + error.message);
    } else {
      toast.success("Vehicle restored to inventory!");
      loadDeletedVehicles();
    }
    setIsProcessing(false);
    setRestoreTarget(null);
  };

  const permanentlyDelete = async (deletedVehicleId: string) => {
    setIsProcessing(true);

    // Get images to clean up storage
    const record = deletedVehicles.find((dv) => dv.id === deletedVehicleId);
    if (record) {
      const images = (record.related_images || []) as any[];
      for (const img of images) {
        const imgUrl = typeof img === "object" && img !== null ? img.image_url : null;
        if (imgUrl) {
          const path = imgUrl.split("/vehicle-images/")[1];
          if (path) await supabase.storage.from("vehicle-images").remove([path]);
        }
      }
    }

    const { error } = await supabase.rpc("permanently_delete_vehicle", {
      p_deleted_vehicle_id: deletedVehicleId,
      p_deleted_by: user?.id || "",
    });

    if (error) {
      toast.error("Failed to permanently delete: " + error.message);
    } else {
      toast.success("Vehicle permanently deleted");
      loadDeletedVehicles();
    }
    setIsProcessing(false);
    setPermanentDeleteTarget(null);
  };

  const getVehicleData = (dv: DeletedVehicle): VehicleSnapshot => {
    const data = dv.vehicle_data as any;
    return {
      year: data?.year || 0,
      make: data?.make || "Unknown",
      model: data?.model || "",
      vin: data?.vin || "N/A",
      price: data?.price || 0,
      mileage: data?.mileage || 0,
      color: data?.color || "",
      status: data?.status || "unknown",
    };
  };

  const getImageCount = (dv: DeletedVehicle): number => {
    const images = dv.related_images as any[];
    return Array.isArray(images) ? images.length : 0;
  };

  const getPrimaryImage = (dv: DeletedVehicle): string | null => {
    const images = dv.related_images as any[];
    if (!Array.isArray(images) || images.length === 0) return null;
    const primary = images.find((img: any) => img.is_primary) || images[0];
    return primary?.image_url || null;
  };

  const formatTimeAgo = (dateStr: string) => {
    const now = new Date();
    const then = new Date(dateStr);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    return then.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/inventory")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-heading font-bold">Recycle Bin</h1>
          <p className="text-muted-foreground">
            Deleted vehicles can be restored or permanently removed
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12">Loading...</div>
      ) : deletedVehicles.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
              <Trash2 className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-1">Recycle bin is empty</h3>
            <p className="text-muted-foreground text-sm">
              Deleted vehicles will appear here and can be restored.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {deletedVehicles.length} deleted vehicle{deletedVehicles.length !== 1 ? "s" : ""}
          </p>
          {deletedVehicles.map((dv) => {
            const vehicle = getVehicleData(dv);
            const vehicleName = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
            const primaryImage = getPrimaryImage(dv);
            const imageCount = getImageCount(dv);

            return (
              <Card key={dv.id} className="overflow-hidden border-dashed border-muted-foreground/30">
                <CardContent className="p-0">
                  <div className="flex flex-col sm:flex-row">
                    {/* Image */}
                    <div className="sm:w-48 w-full h-36 sm:h-auto bg-muted flex-shrink-0 relative overflow-hidden">
                      {primaryImage ? (
                        <img
                          src={primaryImage}
                          alt={vehicleName}
                          className="w-full h-full object-cover opacity-60 grayscale"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          No image
                        </div>
                      )}
                      <div className="absolute top-2 left-2">
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                          Deleted
                        </Badge>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="flex-1 p-4 sm:p-5">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div>
                          <h3 className="text-lg font-heading font-bold">{vehicleName}</h3>
                          <div className="space-y-1 text-sm text-muted-foreground mt-1">
                            <p>VIN: {vehicle.vin}</p>
                            <p>
                              Price: ${vehicle.price.toLocaleString()} · {vehicle.mileage.toLocaleString()} km ·{" "}
                              {vehicle.color}
                            </p>
                            <p className="flex items-center gap-1.5">
                              <Clock className="h-3.5 w-3.5" />
                              Deleted {formatTimeAgo(dv.deleted_at)}
                              {imageCount > 0 && ` · ${imageCount} image${imageCount !== 1 ? "s" : ""} archived`}
                            </p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 sm:flex-shrink-0">
                          <Button
                            size="sm"
                            onClick={() => setRestoreTarget({ id: dv.id, name: vehicleName })}
                            disabled={isProcessing}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            <RotateCcw className="h-4 w-4 mr-1.5" />
                            Restore
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:bg-destructive/10 border-destructive/30"
                            onClick={() => setPermanentDeleteTarget({ id: dv.id, name: vehicleName })}
                            disabled={isProcessing}
                          >
                            <Trash2 className="h-4 w-4 mr-1.5" />
                            Delete Forever
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Restore Confirmation */}
      <ConfirmDialog
        open={!!restoreTarget}
        onOpenChange={(open) => { if (!open) setRestoreTarget(null); }}
        onConfirm={() => restoreTarget && restoreVehicle(restoreTarget.id)}
        title="Restore Vehicle"
        description={restoreTarget ? `Restore "${restoreTarget.name}" back to your inventory? All associated images, expenses, and documents will be recovered.` : ""}
        confirmLabel="Restore Vehicle"
      />

      {/* Permanent Delete Confirmation */}
      <ConfirmDialog
        open={!!permanentDeleteTarget}
        onOpenChange={(open) => { if (!open) setPermanentDeleteTarget(null); }}
        onConfirm={() => permanentDeleteTarget && permanentlyDelete(permanentDeleteTarget.id)}
        title="Permanently Delete"
        description={permanentDeleteTarget ? `This will permanently destroy "${permanentDeleteTarget.name}" and all associated data. Storage files will be removed. This action CANNOT be undone.` : ""}
        confirmLabel="Delete Forever"
        variant="destructive"
      />
    </div>
  );
}
